import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';

import { NgSelectModule } from '@ng-select/ng-select';

import { StateService } from '@/services/state.service';
import { Web3Service } from '@/services/web3.service';
import { TransactionTrackingService } from '@/services/transaction-tracking.service';
import { ThemeService } from '@/services/theme.service';
import { CryptopunksAppService, CryptopunksAppBidData } from '@/services/cryptopunks-app.service';
import { DisguiseBidConfirmationModalComponent } from '@/components/disguise-bid-confirmation-modal/disguise-bid-confirmation-modal.component';
import { OnChainBidLabelComponent } from '@/components/on-chain-bid-label/on-chain-bid-label.component';
import {
  PunksmarketV1Service,
  emptyPunksFilter,
  PunksFilterTuple,
} from '@/services/punksmarket-v1.service';
import {
  buildPunksmarketFilterFromTag,
  isPunksmarketFilterEmpty,
} from '@/utils/punksmarket-filter-criteria.util';
import { onChainBidEscrowHint } from '@/utils/native-market-bid-labels';

import { WalletAddressDirective } from '@/directives/wallet-address.directive';
import { TippyDirective } from '@/directives/tippy.directive';
import { parseEther, formatEther, getAddress, TypedDataDomain } from 'viem';
import { buildMerkleTreeForPunks } from '@/utils/merkle-tree';

import { firstValueFrom, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';

export type BidType = 'collection' | 'trait' | 'owner' | 'targeted';
export type BidStep = 'check' | 'amount' | 'transfer' | 'sign' | 'publish';
export type BidCreationPlatform = 'onchain' | 'stash';

export interface CopyFromBidPayload {
  amountEth: number;
  expiryDays?: number;
  confidential?: boolean;
  limitedPublicNotification?: boolean;
  stashBidProtectionEnabled?: boolean;
  bidType: BidType;
  ownerAddress?: string | null;
  punkIndices?: number[];
  selectedPunkIds?: number[];
  activeTraits?: string[];
  filterTag?: string;
}

@Component({
  selector: 'app-bid-creation-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgSelectModule,
    WalletAddressDirective,
    TippyDirective,
    DisguiseBidConfirmationModalComponent,
    OnChainBidLabelComponent,
  ],
  templateUrl: './bid-creation-modal.component.html',
  styleUrls: ['./bid-creation-modal.component.scss']
})
export class BidCreationModalComponent implements OnChanges, OnDestroy {
  private destroy$ = new Subject<void>();

  // Input properties
  @Input() isActive: boolean = false;
  @Input() bidType: BidType = 'collection';
  @Input() ownerAddress: string | null = null;
  @Input() punkIndices: number[] = [];
  @Input() selectedPunkIds: number[] = [];
  @Input() activeTraits: string[] = [];
  @Input() filterTag: string = '';
  @Input() copyFromBid: CopyFromBidPayload | null = null;

  // Output events
  @Output() modalClosed = new EventEmitter<void>();
  @Output() bidCreated = new EventEmitter<void>();

  // Modal state
  currentStep: BidStep = 'check';
  /** V1 dual-mode: on-chain escrow vs stash bid (default on-chain when both are enabled). */
  selectedBidPlatform: BidCreationPlatform = 'stash';
  dualBidPlatformEnabled = false;
  isOnChainPlatformSelected = false;
  step1Label = 'Stash';
  step3Label = 'Sign message';
  readonly onChainBidEscrowHint = onChainBidEscrowHint;
  onChainSettlementEth = '0';
  showOnChainSettlementReward = false;
  onChainBidSubmitting = false;
  onChainBidPlacedSuccessfully = false;
  placedOnChainTxHash: string | null = null;
  placedOnChainTxShortHash: string | null = null;
  collectionBidAmount: string = '';
  stashAddress: string | null = null;
  stashDeployed: boolean = false;
  /** True when stash bytecode version differs from factory currentVersion (optional first-step upgrade). */
  stashUpgradeNeeded: boolean = false;
  stashOnChainVersion: string | null = null;
  factoryTargetVersion: string | null = null;
  checkingStash: boolean = false;
  isTransactionActive: boolean = false;

  // Stash liquidity state
  stashLiquidityLoading: boolean = false;
  stashLiquidityTotalWei: bigint | null = null;
  stashLiquidityTotalEth: number | null = null;

  // Expiry options
  collectionBidExpiryOptions: { label: string; value: number }[] = [
    { label: '1 hour', value: 1/24 },
    { label: '1 day', value: 1 },
    { label: '3 days', value: 3 },
    { label: '7 days', value: 7 },
    { label: '30 days', value: 30 },
    { label: '1 year', value: 365 }
  ];
  collectionBidExpiryModel = this.collectionBidExpiryOptions[2];

  // Confidential bid option
  confidential: boolean = false;
  sendLimitedNotification: boolean = true;

  /** When true, trait bids are published with custom-bid metadata (tag + bid_type) while the on-chain Merkle set stays trait-filtered. */
  disguiseTraitBidAsCustom: boolean = false;

  /** Shown when the user turns disguise bid on; confirm sets disguiseTraitBidAsCustom to true. */
  disguiseBidConfirmationActive: boolean = false;

  // Publish on cryptopunks.app option
  publishOnCryptopunksApp: boolean = true;

  /** When STASH_BID_PROTECTION feature flag is on: auto-reset competing legacy bids (default on). */
  stashBidProtectionEnabled: boolean = true;

  showStashBidProtectionToggle: boolean =
    environment.featureFlags?.STASH_BID_PROTECTION === true &&
    (environment.collection === 'cryptopunks' || environment.collection === 'cryptopunks-v1');

  // Publishing status (for the confirmation step)
  publishStatusLimo: 'pending' | 'loading' | 'success' | 'error' | 'skipped' = 'pending';
  publishStatusApp: 'pending' | 'loading' | 'success' | 'error' = 'pending';
  publishErrorLimo: string = '';
  publishErrorApp: string = '';
  showCryptopunksAppPublish: boolean = false;
  private cryptopunksAppPayload: CryptopunksAppBidData | null = null;

  // Expose parseFloat for template
  parseFloat = parseFloat;

  protected readonly environment = environment;

  constructor(
    public stateSvc: StateService,
    private web3Svc: Web3Service,
    public txTrackingSvc: TransactionTrackingService,
    public themeSvc: ThemeService,
    private http: HttpClient,
    private cryptopunksAppService: CryptopunksAppService,
    private punksmarketV1Svc: PunksmarketV1Service,
  ) {
    this.refreshDualBidPlatformFlags();
  }

  private static readonly EXPIRY_DAY_OPTIONS = [1, 3, 7, 30, 365];

  /**
   * Punks owned by this wallet are prepended (random subset) to disguised trait bids so notifications
   * show decoy IDs first; must match listings-scraper thumbnail wallet.
   */
  private static readonly UI_MASKED_TRAIT_THUMBNAIL_WALLET =
    '0xa858ddc0445d8131dac4d1de01f834ffcba52ef1';
  private static readonly UI_MASKED_TRAIT_DECOY_COUNT = 35;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isActive'] && this.isActive) {
      this.resetModalState();
      if (this.isOnChainPlatformSelected) {
        this.checkingStash = false;
      } else {
        this.checkStashStatus();
      }
      this.applyCopyFromBid();
    }
  }

  private refreshDualBidPlatformFlags(): void {
    this.dualBidPlatformEnabled =
      environment.collection === 'cryptopunks-v1' &&
      environment.featureFlags?.STASH_BID_SUPPORT !== false &&
      this.punksmarketV1Svc.isBidNativeEnabled();
  }

  private syncStepperLabels(): void {
    if (this.dualBidPlatformEnabled) {
      this.step1Label = 'Type';
      this.step3Label = this.isOnChainPlatformSelected ? 'Transaction' : 'Sign message';
    } else {
      this.step1Label = 'Stash';
      this.step3Label = 'Sign message';
    }
  }

  selectBidPlatform(platform: BidCreationPlatform): void {
    if (!this.dualBidPlatformEnabled || this.selectedBidPlatform === platform) {
      return;
    }
    this.selectedBidPlatform = platform;
    this.isOnChainPlatformSelected = platform === 'onchain';
    this.syncStepperLabels();
    if (platform === 'stash' && !this.checkingStash && !this.stashDeployed) {
      void this.checkStashStatus();
    }
  }

  onOnChainSettlementToggle(): void {
    this.showOnChainSettlementReward = !this.showOnChainSettlementReward;
    if (!this.showOnChainSettlementReward) {
      this.onChainSettlementEth = '0';
    }
  }

  proceedFromTypeStep(): void {
    if (this.isOnChainPlatformSelected) {
      this.proceedToAmountInput();
      return;
    }
    if (this.stashUpgradeNeeded) {
      void this.upgradeStashContract();
      return;
    }
    if (this.stashDeployed) {
      this.proceedToAmountInput();
      return;
    }
    void this.deployStash();
  }

  proceedFromAmountStep(): void {
    if (this.isOnChainPlatformSelected) {
      this.currentStep = 'sign';
      return;
    }
    void this.transferFundsToStash();
  }

  private applyCopyFromBid(): void {
    if (!this.copyFromBid) return;

    this.collectionBidAmount = String(this.copyFromBid.amountEth);
    this.confidential = this.copyFromBid.confidential ?? false;
    this.sendLimitedNotification = this.copyFromBid.limitedPublicNotification ?? true;
    this.stashBidProtectionEnabled = this.copyFromBid.stashBidProtectionEnabled ?? true;
    if (this.confidential) {
      this.publishOnCryptopunksApp = false;
    } else {
      this.publishOnCryptopunksApp = true;
    }

    if (this.copyFromBid.expiryDays != null) {
      const closest = BidCreationModalComponent.EXPIRY_DAY_OPTIONS.reduce((prev, curr) =>
        Math.abs(curr - this.copyFromBid!.expiryDays!) < Math.abs(prev - this.copyFromBid!.expiryDays!) ? curr : prev
      );
      const match = this.collectionBidExpiryOptions.find(o => o.value === closest);
      if (match) {
        this.collectionBidExpiryModel = match;
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Get the modal title based on bid type
  getModalTitle(): string {
    switch (this.bidType) {
      case 'owner':
        return 'Create Owner Bid';
      case 'trait':
        return 'Create Trait Bid';
      case 'targeted':
        return 'Create Custom Bid';
      default:
        return 'Create Collection Bid';
    }
  }

  // Get bid type label for display
  getBidTypeLabel(): string {
    switch (this.bidType) {
      case 'owner':
        return 'Owner bid';
      case 'trait':
        return 'Trait bid';
      case 'targeted':
        return 'Custom bid';
      default:
        return 'Collection (unfiltered)';
    }
  }

  // Check stash status
  async checkStashStatus(): Promise<void> {
    try {
      this.checkingStash = true;
      const walletAddress = this.stateSvc.getWalletAddress();
      if (!walletAddress) {
        throw new Error('No wallet connected');
      }

      // Get stash address
      try {
        this.stashAddress = await this.web3Svc.getStashAddressFor(walletAddress);
      } catch (error) {
        console.log('[BidCreationModal] No stash address found, will need to deploy');
        this.stashAddress = null;
      }

      // Check if deployed
      if (this.stashAddress) {
        this.stashDeployed = await this.web3Svc.checkStashDeployed(walletAddress);
      } else {
        this.stashDeployed = false;
      }

      this.stashUpgradeNeeded = false;
      this.stashOnChainVersion = null;
      this.factoryTargetVersion = null;

      if (this.stashAddress && this.stashDeployed) {
        const up = await this.web3Svc.getStashUpgradeStatus(this.stashAddress);
        this.stashUpgradeNeeded = up.needsUpgrade;
        this.stashOnChainVersion = up.stashVersion.toString();
        this.factoryTargetVersion = up.factoryCurrentVersion.toString();
        await this.loadStashAvailableLiquidity();
      } else {
        this.clearStashLiquidityState();
      }

      this.checkingStash = false;
    } catch (error) {
      console.error('[BidCreationModal] Error checking stash status:', error);
      this.checkingStash = false;
      this.txTrackingSvc.setErrorTransactionMessage(error, '<p>Error checking stash status.</p>');
    }
  }

  private clearStashLiquidityState(): void {
    this.stashLiquidityTotalWei = null;
    this.stashLiquidityTotalEth = null;
    this.stashLiquidityLoading = false;
  }

  private async loadStashAvailableLiquidity(): Promise<void> {
    if (!this.stashAddress) {
      this.clearStashLiquidityState();
      return;
    }

    this.stashLiquidityLoading = true;
    try {
      const totalWei = await this.web3Svc.getStashAvailableLiquidity(this.stashAddress);
      this.stashLiquidityTotalWei = totalWei;
      this.stashLiquidityTotalEth = parseFloat(formatEther(totalWei));
    } catch (error) {
      console.error('[BidCreationModal] Error fetching stash liquidity:', error);
      this.clearStashLiquidityState();
    } finally {
      this.stashLiquidityLoading = false;
    }
  }

  // Deploy stash
  async deployStash(): Promise<void> {
    try {
      const walletAddress = this.stateSvc.getWalletAddress();
      if (!walletAddress) {
        throw new Error('No wallet connected');
      }

      this.isTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage('<p>Please confirm the stash deployment transaction in your wallet.</p>');

      const hash = await this.web3Svc.deployStash(walletAddress);
      if (!hash) {
        throw new Error('Transaction hash not returned');
      }

      this.txTrackingSvc.setTransactionMessage(hash, '<p>Your stash deployment transaction is being processed.</p>');

      const receipt = await this.web3Svc.waitForTransaction(hash);
      this.txTrackingSvc.setProcessTransactionMessage(receipt, '<p>Your stash has been deployed successfully!</p>');

      // Update stash status
      await this.checkStashStatus();

      // Close transaction modal and proceed to next step
      setTimeout(() => {
        this.txTrackingSvc.closeTxModal();
        this.isTransactionActive = false;
        this.proceedToAmountInput();
      }, 2000);
    } catch (error) {
      console.error('[BidCreationModal] Error deploying stash:', error);
      this.txTrackingSvc.setErrorTransactionMessage(error, '<p>Error deploying stash.</p>');
      setTimeout(() => {
        this.isTransactionActive = false;
      }, 3000);
    }
  }

  /** Upgrade stash proxy to factory latest implementation (optional; caller must be stash owner). */
  async upgradeStashContract(): Promise<void> {
    try {
      this.isTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage('<p>Please confirm the stash upgrade transaction in your wallet.</p>');

      const hash = await this.web3Svc.upgradeStash();
      if (!hash) {
        throw new Error('Transaction hash not returned');
      }

      this.txTrackingSvc.setTransactionMessage(hash, '<p>Your stash upgrade is being processed.</p>');

      const receipt = await this.web3Svc.waitForTransaction(hash);
      this.txTrackingSvc.setProcessTransactionMessage(receipt, '<p>Your stash has been upgraded successfully.</p>');

      await this.checkStashStatus();

      setTimeout(() => {
        this.txTrackingSvc.closeTxModal();
        this.isTransactionActive = false;
      }, 2000);
    } catch (error) {
      console.error('[BidCreationModal] Error upgrading stash:', error);
      this.txTrackingSvc.setErrorTransactionMessage(error, '<p>Error upgrading stash.</p>');
      setTimeout(() => {
        this.isTransactionActive = false;
      }, 3000);
    }
  }

  onConfidentialToggle(): void {
    this.confidential = !this.confidential;
    if (this.confidential) {
      this.publishOnCryptopunksApp = false;
      this.sendLimitedNotification = true;
    } else {
      this.publishOnCryptopunksApp = true;
    }
  }

  onSendLimitedNotificationToggle(): void {
    this.sendLimitedNotification = !this.sendLimitedNotification;
  }


  onStashBidProtectionToggle(): void {
    this.stashBidProtectionEnabled = !this.stashBidProtectionEnabled;
  }

  stopToggleClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
  }

  onDisguiseTraitBidToggle(): void {
    if (this.disguiseTraitBidAsCustom) {
      this.disguiseTraitBidAsCustom = false;
      return;
    }
    this.disguiseBidConfirmationActive = true;
  }

  /** Opens the disguise bid confirmation modal without toggling the switch. */
  onDisguiseBidInfoClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.disguiseBidConfirmationActive = true;
  }

  onDisguiseBidConfirmationConfirm(): void {
    this.disguiseTraitBidAsCustom = true;
    this.disguiseBidConfirmationActive = false;
  }

  onDisguiseBidConfirmationCancel(): void {
    this.disguiseBidConfirmationActive = false;
  }

  proceedToAmountInput(): void {
    this.currentStep = 'amount';
    // Reload stash balance when entering amount step to ensure it's up to date
    if (this.stashAddress && this.stashDeployed) {
      this.loadStashAvailableLiquidity();
    }
  }

  // Transfer funds to stash
  async transferFundsToStash(): Promise<void> {
    try {
      if (!this.collectionBidAmount || parseFloat(this.collectionBidAmount) <= 0) {
        throw new Error('Invalid bid amount');
      }

      if (!this.stashAddress) {
        throw new Error('Stash address not available');
      }

      const walletAddress = this.stateSvc.getWalletAddress();
      if (!walletAddress) {
        throw new Error('No wallet connected');
      }

      // Convert ETH to wei
      const amountString = String(this.collectionBidAmount).trim();
      if (!amountString || isNaN(parseFloat(amountString))) {
        throw new Error('Invalid bid amount format');
      }
      const amountWeiBigInt = parseEther(amountString);
      const availableWei = this.stashLiquidityTotalWei ?? BigInt(0);

      if (availableWei >= amountWeiBigInt /* || availableWei !== BigInt(0) */) {
        console.log('[BidCreationModal] Stash already has sufficient liquidity; skipping transfer step.');
        this.currentStep = 'sign';
        return;
      }

      const shortfallWei = amountWeiBigInt - availableWei;
      this.currentStep = 'transfer';
      const shortfallWeiString = shortfallWei.toString();

      this.isTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage('<p>Please confirm the transfer transaction in your wallet.</p>');

      const hash = await this.web3Svc.sendEthToAddress(this.stashAddress, shortfallWeiString);
      if (!hash) {
        throw new Error('Transaction hash not returned');
      }

      this.txTrackingSvc.setTransactionMessage(hash, '<p>Your transfer transaction is being processed.</p>');

      const receipt = await this.web3Svc.waitForTransaction(hash);
      this.txTrackingSvc.setProcessTransactionMessage(receipt, '<p>Funds have been transferred to your stash successfully!</p>');

      try {
        await this.loadStashAvailableLiquidity();
      } catch (liquidityError) {
        console.error('[BidCreationModal] Error refreshing stash liquidity after transfer:', liquidityError);
      }

      // Close transaction modal and proceed to signature step
      setTimeout(() => {
        this.txTrackingSvc.closeTxModal();
        this.isTransactionActive = false;
        this.currentStep = 'sign';
      }, 2000);
    } catch (error) {
      console.error('[BidCreationModal] Error transferring funds:', error);
      this.txTrackingSvc.setErrorTransactionMessage(error, '<p>Error transferring funds to stash.</p>');
      setTimeout(() => {
        this.isTransactionActive = false;
      }, 3000);
    }
  }

  // Withdraw available ETH from stash
  async withdrawAvailableEth(): Promise<void> {
    try {
      if (!this.stashAddress) {
        throw new Error('Stash address not available');
      }

      const stashAddress = this.stashAddress;
      this.closeModal();

      const availableEthWei = await this.web3Svc.getStashAvailableEth(stashAddress);
      if (!availableEthWei || availableEthWei === BigInt(0)) {
        this.txTrackingSvc.setErrorTransactionMessage(
          { message: 'No ETH available to withdraw.' },
          '<p>No ETH available to withdraw from your stash.</p>'
        );
        return;
      }

      this.isTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage('<p>Please confirm the withdraw transaction in your wallet.</p>');

      const hash = await this.web3Svc.withdrawEthFromStash(stashAddress, availableEthWei);
      if (!hash) {
        throw new Error('Transaction hash not returned');
      }

      this.txTrackingSvc.setTransactionMessage(hash, '<p>Your withdraw transaction is being processed.</p>');

      const receipt = await this.web3Svc.waitForTransaction(hash);
      this.txTrackingSvc.setProcessTransactionMessage(receipt, '<p>ETH withdrawn from your stash successfully!</p>');
      setTimeout(() => {
        this.isTransactionActive = false;
      }, 3000);
    } catch (error) {
      console.error('[BidCreationModal] Error withdrawing ETH from stash:', error);
      this.txTrackingSvc.setErrorTransactionMessage(error, '<p>Error withdrawing ETH from stash.</p>');
      setTimeout(() => {
        this.isTransactionActive = false;
      }, 3000);
    }
  }

  private static readonly CRYPTOPUNKS_APP_TYPE_MAP: Record<string, string> = {
    guy: 'Male',
    male: 'Male',
    girl: 'Female',
    female: 'Female',
    fem: 'Female',
    ape: 'Ape',
    zombie: 'Zombie',
    alien: 'Alien',
  };

  private static readonly CRYPTOPUNKS_APP_SPECIAL_TRAIT_MAP: Record<string, string> = {
    '3d glasses': '3D Glasses',
    'do rag': 'Do-rag',
    'do-rag': 'Do-rag',
    vr: 'VR',
  };

  /**
   * Converts our internal URLSearchParams-style filterTag into the comma-separated
   * canonical trait format expected by the cryptopunks.app POST /api/v1/bids endpoint.
   *
   * Input example:  "type=guy&skintone=1&traitCount=7"  or  "type=guy&trait=hoodie"
   * Output example: "Male, 7 Traits"                    or  "Male, Hoodie"
   */
  private tagForCryptopunksAppPublish(tag: string): string | undefined {
    const isListBid =
      this.bidType === 'targeted' ||
      (this.bidType === 'trait' && this.disguiseTraitBidAsCustom);
    if (isListBid) {
      return undefined;
    }

    if (!tag || !tag.trim()) {
      return undefined;
    }

    const params = new URLSearchParams(tag);
    params.delete('skintone');

    const parts: string[] = [];

    params.forEach((value, key) => {
      const lowerKey = key.toLowerCase();
      const lowerValue = value.trim().toLowerCase();

      if (!lowerValue) {
        return;
      }

      if (lowerKey === 'traitcount') {
        const n = parseInt(value, 10);
        if (!isNaN(n) && n > 0) {
          parts.push(n === 1 ? '1 Trait' : `${n} Traits`);
        }
        return;
      }

      if (lowerKey === 'type' || lowerKey === 'specialtrait') {
        const mapped = BidCreationModalComponent.CRYPTOPUNKS_APP_TYPE_MAP[lowerValue];
        if (mapped) {
          parts.push(mapped);
        }
        return;
      }

      const special = BidCreationModalComponent.CRYPTOPUNKS_APP_SPECIAL_TRAIT_MAP[lowerValue];
      if (special) {
        parts.push(special);
        return;
      }

      parts.push(
        value
          .trim()
          .replace(/[_\-]/g, ' ')
          .split(' ')
          .filter(Boolean)
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(' '),
      );
    });

    const result = parts.join(', ');
    return result || undefined;
  }

  private getNftIndexerRestBaseUrl(): string {
    const graphUri = this.environment.alternativeGraphURI?.trim() || '';
    if (!graphUri) {
      throw new Error(
        'alternativeGraphURI is not configured; cannot load decoy punk IDs for disguised trait bid',
      );
    }
    return graphUri.replace(/\/graphql\/?$/i, '').replace(/\/$/, '');
  }

  /** `collections` query param for GET /owned (aligned with sales-indexer / listings-scraper). */
  private getOwnedApiCollectionsParam(): string {
    const e = this.environment;
    if (e.collection === 'cryptopunks-v1') {
      return [e.wrappedPunksAddress, e.v1PunksMarketAddress || e.punksMarketAddress].join(',');
    }
    if (e.collection === 'crypto-phunks') {
      return e.wrappedPunksAddress;
    }
    const parts = [e.wrappedPunksAddress, e.punksMarketAddress];
    if (e.c721Address && e.c721Address !== '0x0000000000000000000000000000000000000000') {
      parts.push(e.c721Address);
    }
    return parts.join(',');
  }

  private sampleWithoutReplacement(pool: number[], k: number): number[] {
    const copy = [...pool];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j]!, copy[i]!];
    }
    return copy.slice(0, k);
  }

  /**
   * Random punk IDs from the thumbnail wallet that are not in the trait Merkle set (avoids duplicate leaves).
   * Throws if the wallet has fewer than 35 owned punks or fewer than 35 outside the bid set.
   */
  private async fetchRandomDecoyPunkIdsForDisguisedTraitBid(traitIndicesSorted: number[]): Promise<number[]> {
    const traitSet = new Set(traitIndicesSorted);
    const wallet = BidCreationModalComponent.UI_MASKED_TRAIT_THUMBNAIL_WALLET;
    const need = BidCreationModalComponent.UI_MASKED_TRAIT_DECOY_COUNT;
    const baseUrl = this.getNftIndexerRestBaseUrl();
    const collections = this.getOwnedApiCollectionsParam();
    const url = `${baseUrl}/owned/${encodeURIComponent(wallet.toLowerCase())}`;
    const res = await firstValueFrom(
      this.http.get<Array<{ tokenId?: string | number }>>(url, {
        params: { collections },
        headers: { Accept: 'application/json' },
      }),
    );
    if (!Array.isArray(res)) {
      throw new Error('Owned punks API returned an invalid response');
    }
    const ownedUnique = new Set<number>();
    for (const item of res) {
      if (item?.tokenId === undefined) continue;
      const n = parseInt(String(item.tokenId), 10);
      if (!Number.isNaN(n) && n >= 0) {
        ownedUnique.add(n);
      }
    }
    if (ownedUnique.size < need) {
      throw new Error(
        `Thumbnail wallet must own at least ${need} punks for disguised trait bids (got ${ownedUnique.size})`,
      );
    }
    const pool = Array.from(ownedUnique).filter((id) => !traitSet.has(id));
    if (pool.length < need) {
      throw new Error(
        `Need at least ${need} thumbnail-wallet punks outside the bid set for Merkle uniqueness (got ${pool.length})`,
      );
    }
    return this.sampleWithoutReplacement(pool, need);
  }

  // Sign punk bid message
  async signPunkBidMessage(): Promise<void> {
    try {
      const walletAddress = this.stateSvc.getWalletAddress();
      if (!walletAddress) {
        throw new Error('No wallet connected');
      }

      if (!this.collectionBidAmount || parseFloat(this.collectionBidAmount) <= 0) {
        throw new Error('Invalid bid amount');
      }

      // Convert ETH to wei
      const amountString = String(this.collectionBidAmount).trim();
      if (!amountString || isNaN(parseFloat(amountString))) {
        throw new Error('Invalid bid amount format');
      }
      const amountWeiBigInt = parseEther(amountString);
      const amountWei = amountWeiBigInt.toString();

      let disguisedTraitDecoyPrefix: number[] = [];
      if (this.bidType === 'trait' && this.disguiseTraitBidAsCustom && this.punkIndices.length > 0) {
        const traitBaseSorted = [...this.punkIndices].sort((a, b) => a - b);
        disguisedTraitDecoyPrefix = await this.fetchRandomDecoyPunkIdsForDisguisedTraitBid(traitBaseSorted);
      }

      // Determine punk indices and filter tag based on bid type
      let indices: number[] = [];
      let tag: string = this.filterTag;
      let merkleRoot: `0x${string}` = '0x0000000000000000000000000000000000000000000000000000000000000000';
      let proofs: Record<number, `0x${string}`[]> = {};
      let isTargetedBid: boolean = false;

      if (this.bidType === 'owner' && this.punkIndices.length > 0) {
        // Owner bid: use punk indices from the owned punks
        indices = [...this.punkIndices].sort((a, b) => a - b);
        tag = this.filterTag || `owner=${this.ownerAddress?.toLowerCase()}`;

        // Build Merkle tree for owner bid
        const merkleData = buildMerkleTreeForPunks(indices);
        merkleRoot = merkleData.root;
        proofs = merkleData.proofs;

        console.log('[BidCreationModal] Owner bid - punk count:', indices.length, 'tag:', tag);
        console.log('[BidCreationModal] Merkle root:', merkleRoot);
      } else if (this.bidType === 'targeted' && this.selectedPunkIds.length > 0) {
        // Targeted bid: use selected punk IDs
        indices = [...this.selectedPunkIds].sort((a, b) => a - b);
        isTargetedBid = true;
        tag = 'targeted';

        // Build Merkle tree for targeted bid
        const merkleData = buildMerkleTreeForPunks(indices);
        merkleRoot = merkleData.root;
        proofs = merkleData.proofs;

        console.log('[BidCreationModal] Targeted bid - selected punks:', indices.length);
        console.log('[BidCreationModal] Merkle root:', merkleRoot);
      } else if (this.bidType === 'trait' && this.punkIndices.length > 0) {
        // Trait bid: use filtered indices (disguised bids prepend decoy wallet punks before signing)
        const baseTrait = [...this.punkIndices].sort((a, b) => a - b);
        indices =
          this.disguiseTraitBidAsCustom && disguisedTraitDecoyPrefix.length > 0
            ? [...disguisedTraitDecoyPrefix, ...baseTrait]
            : baseTrait;
        tag = this.filterTag;

        // Build Merkle tree for trait bid
        const merkleData = buildMerkleTreeForPunks(indices);
        merkleRoot = merkleData.root;
        proofs = merkleData.proofs;

        console.log('[BidCreationModal] Trait bid - indices:', indices.length, 'tag:', tag);
        console.log('[BidCreationModal] Merkle root:', merkleRoot);
      } else if (this.bidType === 'collection') {
        // Collection-wide: keep zero root in EIP-712 (matches cryptopunks.app expectation); POST still sends punkIndices 0..9999.
        merkleRoot = '0x0000000000000000000000000000000000000000000000000000000000000000';
        proofs = {};
        console.log('[BidCreationModal] Collection bid — forced zero Merkle root');
      }

      const publishAsCustomBidMetadata =
        isTargetedBid || (this.bidType === 'trait' && this.disguiseTraitBidAsCustom);
      const saveFilterTag =
        this.bidType === 'trait' && this.disguiseTraitBidAsCustom ? 'targeted' : tag;

      const accountNonce = await this.getNextCollectionBidAccountNonce(walletAddress);
      const bidNonce = Date.now();
      const selectedExpiryDays = this.collectionBidExpiryModel?.value ?? 7;
      // Calculate expiration timestamp in UTC (Unix timestamp in seconds)
      const expirationTimestamp = Math.floor(Date.now() / 1000) + (selectedExpiryDays * 24 * 60 * 60);
      // Convert to ISO string in UTC
      const expirationIso = new Date(expirationTimestamp * 1000).toISOString();

      const { signature, typedData, verifyingContract } = await this.web3Svc.signPunkBidMessage({
        verifyingContract: this.stashAddress as `0x${string}`,
        root: merkleRoot,
        accountNonce,
        bidNonce,
        expiration: expirationTimestamp,
        order: {
          numberOfUnits: 1,
          pricePerUnit: amountWei,
          // For V1, use wrappedPunksAddress; for V2, use punksMarketAddress
          auction: (this.environment.collection === 'cryptopunks-v1'
            ? this.environment.wrappedPunksAddress
            : this.environment.punksMarketAddress) as `0x${string}`
        }
      });

      const punkBidPayload = {
        order: {
          numberOfUnits: typedData.message.order.numberOfUnits,
          pricePerUnit: typedData.message.order.pricePerUnit.toString(),
          auction: typedData.message.order.auction
        },
        accountNonce: typedData.message.accountNonce.toString(),
        bidNonce: typedData.message.bidNonce.toString(),
        expiration: typedData.message.expiration.toString(),
        root: typedData.message.root,
        verifyingContract
      };

      console.log('[CPAPP-DEBUG] Signed EIP-712 typed data: ' + JSON.stringify({
        domain: typedData.domain,
        primaryType: (typedData as any).primaryType,
        message: {
          order: {
            numberOfUnits: typedData.message.order.numberOfUnits,
            pricePerUnit: typedData.message.order.pricePerUnit.toString(),
            auction: typedData.message.order.auction,
          },
          accountNonce: typedData.message.accountNonce.toString(),
          bidNonce: typedData.message.bidNonce.toString(),
          expiration: typedData.message.expiration.toString(),
          root: typedData.message.root,
        },
        merkleRoot,
        signature,
        signerAddress: walletAddress,
        expirationTimestamp,
        expirationIso,
      }, null, 2));

      // Move to publish confirmation step
      this.showCryptopunksAppPublish =
        !this.confidential && this.environment.collection === 'cryptopunks';
      const persistCollectionBidToListingsScraper =
        this.shouldPersistCollectionBidToListingsScraper(this.confidential);
      this.publishStatusLimo = persistCollectionBidToListingsScraper ? 'loading' : 'skipped';
      this.publishStatusApp = this.showCryptopunksAppPublish ? 'loading' : 'pending';
      this.publishErrorLimo = '';
      this.publishErrorApp = '';
      this.currentStep = 'publish';

      // Publish to listings scraper (orderbook UI: v1cryptopunks.com vs cryptopunks.eth.limo)
      try {
        await this.saveCollectionBid({
          bidderAddress: walletAddress,
          bidAmountWei: amountWei,
          bidAmountEth: parseFloat(this.collectionBidAmount),
          merkleRoot,
          accountNonce,
          bidNonce,
          signature,
          punk_indices: indices,
          filterTag: saveFilterTag,
          proofs: proofs,
          punkBid: punkBidPayload,
          typedDataDomain: typedData.domain,
          expirationTimestamp,
          expiresAtIso: expirationIso,
          isTargetedBid: publishAsCustomBidMetadata,
          selectedPunkIds: indices,
          bidType: this.bidType,
          confidential: this.confidential,
          limitedPublicNotification: this.sendLimitedNotification,
          stashBidProtectionEnabled: this.showStashBidProtectionToggle ? this.stashBidProtectionEnabled : undefined,
        });
        this.publishStatusLimo = persistCollectionBidToListingsScraper ? 'success' : 'skipped';
        if (persistCollectionBidToListingsScraper) {
          this.stateSvc.requestOutgoingBidsRefresh();
        }
      } catch (limoError: any) {
        this.publishStatusLimo = 'error';
        this.publishErrorLimo = limoError?.message || 'Unknown error';
      }

      // Publish to cryptopunks.app if enabled (never for confidential bids)
      if (this.showCryptopunksAppPublish) {
        let punkIndicesForCryptopunksApp: number[];
        if (this.bidType === 'collection' && indices.length === 0) {
          punkIndicesForCryptopunksApp = Array.from({ length: 10000 }, (_, i) => i);
        } else {
          punkIndicesForCryptopunksApp = indices;
        }

        const tagForCryptopunksApp = this.tagForCryptopunksAppPublish(tag);
        const stashForCpapp = (this.stashAddress || '').trim();
        // `tag` is bids API display metadata only; PunkBid EIP-712 has no tag field.
        // Checksum addresses to match cryptopunks.app JSON (unlikely to affect EIP-712, avoids picky backends).
        this.cryptopunksAppPayload = {
          punkIndices: punkIndicesForCryptopunksApp,
          bidAmount: String(this.collectionBidAmount),
          address: getAddress(walletAddress as `0x${string}`),
          signature: signature,
          chainId: this.environment.chainId,
          auctionContract: getAddress(this.environment.punksMarketAddress as `0x${string}`),
          stashContract: stashForCpapp ? getAddress(stashForCpapp as `0x${string}`) : '',
          // Match cryptopunks.app client: ISO 8601 (they parse this to the same expiration seconds in EIP-712).
          expiresAt: expirationIso,
          bidNonce: bidNonce,
          ...(tagForCryptopunksApp ? { tag: tagForCryptopunksApp } : {}),
          ...(this.showStashBidProtectionToggle
            ? { stashBidProtectionEnabled: this.stashBidProtectionEnabled }
            : {}),
        };

        const expiresAtParsedUnixSeconds = Math.floor(
          new Date(expirationIso).getTime() / 1000,
        );
        const expirationMatchesExpiresAt =
          expiresAtParsedUnixSeconds === expirationTimestamp;
        if (!expirationMatchesExpiresAt) {
          console.warn(
            '[CPAPP-SUPPORT] expiration mismatch: signed unix !== floor(Date.parse(expiresAt)/1000)',
            { expirationTimestamp, expirationIso, expiresAtParsedUnixSeconds },
          );
        }

        const cpappSupportBundle = {
          generatedAt: new Date().toISOString(),
          note:
            'Same wallet session: signTypedData matches signature; postBody is sent to POST /api/v1/bids (via listings-scraper proxy). Filter console by CPAPP-SUPPORT.',
          expirationCheck: {
            signedExpirationUnixSeconds: expirationTimestamp,
            expiresAtIsoInPostBody: expirationIso,
            expiresAtParsedToUnixSeconds: expiresAtParsedUnixSeconds,
            aligned: expirationMatchesExpiresAt,
          },
          postBody: this.cryptopunksAppPayload,
          signTypedData: {
            domain: typedData.domain,
            primaryType: (typedData as { primaryType: string }).primaryType,
            types: typedData.types,
            message: {
              order: {
                numberOfUnits: typedData.message.order.numberOfUnits,
                pricePerUnit: typedData.message.order.pricePerUnit.toString(),
                auction: typedData.message.order.auction,
              },
              accountNonce: typedData.message.accountNonce.toString(),
              bidNonce: typedData.message.bidNonce.toString(),
              expiration: typedData.message.expiration.toString(),
              root: typedData.message.root,
            },
          },
          signature,
        };
        console.log(`[CPAPP-SUPPORT] ${JSON.stringify(cpappSupportBundle)}`);

        console.log('[CPAPP-DEBUG] Payload sent to cryptopunks.app:', JSON.stringify(this.cryptopunksAppPayload, null, 2));

        try {
          await this.cryptopunksAppService.publishBid(this.cryptopunksAppPayload);
          this.publishStatusApp = 'success';
        } catch (appError: any) {
          this.publishStatusApp = 'error';
          this.publishErrorApp = appError?.message || 'Unknown error';
        }
      }
    } catch (error) {
      console.error('[BidCreationModal] Error signing collection bid message:', error);
      this.txTrackingSvc.setErrorTransactionMessage(error, '<p>Error signing collection bid message.</p>');
    }
  }

  async retryCryptopunksAppPublish(): Promise<void> {
    if (!this.cryptopunksAppPayload) return;
    this.publishStatusApp = 'loading';
    this.publishErrorApp = '';
    try {
      await this.cryptopunksAppService.publishBid(this.cryptopunksAppPayload);
      this.publishStatusApp = 'success';
    } catch (appError: any) {
      this.publishStatusApp = 'error';
      this.publishErrorApp = appError?.message || 'Unknown error';
    }
  }

  private buildOnChainIncludeIds(): number[] {
    if (this.bidType === 'targeted' && this.selectedPunkIds.length > 0) {
      return [...this.selectedPunkIds].sort((a, b) => a - b);
    }
    if (this.bidType === 'owner' && this.punkIndices.length > 0) {
      return [...this.punkIndices].sort((a, b) => a - b);
    }
    return [];
  }

  private buildOnChainCriteria(): PunksFilterTuple {
    if (this.bidType !== 'trait') {
      return emptyPunksFilter();
    }

    const criteria = buildPunksmarketFilterFromTag(this.filterTag);
    if (isPunksmarketFilterEmpty(criteria)) {
      throw new Error(
        'Unable to map the active trait filters to on-chain bid criteria.',
      );
    }
    return criteria;
  }

  async submitOnChainBid(): Promise<void> {
    if (this.onChainBidSubmitting) {
      return;
    }

    const amountString = String(this.collectionBidAmount).trim();
    const amount = parseFloat(amountString);
    if (!amountString || Number.isNaN(amount) || amount <= 0) {
      this.txTrackingSvc.setErrorTransactionMessage(
        { message: 'Invalid bid amount' },
        '<p>Enter a bid amount greater than zero.</p>',
      );
      return;
    }

    this.onChainBidSubmitting = true;
    this.isTransactionActive = true;
    this.txTrackingSvc.initTransactionMessage(
      '<p>Please confirm the on-chain bid transaction in your wallet.</p>',
    );

    try {
      const settlementStr = String(this.onChainSettlementEth ?? '0').trim() || '0';
      const bidWei = parseEther(amountString);
      const settlementWei = parseEther(settlementStr);
      const includeIds = this.buildOnChainIncludeIds();
      const criteria = this.buildOnChainCriteria();

      const hash = await this.punksmarketV1Svc.placeBid({
        bidWei,
        settlementWei,
        criteria,
        includeIds,
        excludeIds: [],
      });

      if (!hash) {
        throw new Error('Transaction hash not returned');
      }

      this.txTrackingSvc.setTransactionMessage(
        hash,
        '<p>Your on-chain bid transaction is being processed.</p>',
      );

      const receipt = await this.web3Svc.waitForTransaction(hash);
      this.txTrackingSvc.setProcessTransactionMessage(
        receipt,
        '<p>Your on-chain bid is live.</p>',
      );

      this.placedOnChainTxHash = hash;
      this.placedOnChainTxShortHash =
        hash.length >= 12 ? `${hash.slice(0, 6)}...${hash.slice(-6)}` : hash;
      this.onChainBidPlacedSuccessfully = true;
      this.currentStep = 'publish';
      this.stateSvc.requestOutgoingBidsRefresh();

      setTimeout(() => {
        this.txTrackingSvc.closeTxModal();
        this.isTransactionActive = false;
      }, 2000);
    } catch (error) {
      console.error('[BidCreationModal] Error placing on-chain bid:', error);
      this.txTrackingSvc.setErrorTransactionMessage(
        error,
        '<p>Error placing on-chain bid.</p>',
      );
      setTimeout(() => {
        this.isTransactionActive = false;
      }, 3000);
    } finally {
      this.onChainBidSubmitting = false;
    }
  }

  private async getNextCollectionBidAccountNonce(walletAddress: string): Promise<number> {
    const padding = 0;
    try {
      const onchainNonce = await this.web3Svc.getCollectionBidAccountNonce(walletAddress);
      return onchainNonce + padding;
    } catch (error) {
      console.warn('[BidCreationModal] Unable to fetch on-chain account nonce, using padding only.', error);
      return padding;
    }
  }

  /**
   * V1 marketplace always POSTs collection bids to the listings scraper (source v1cryptopunks.com in payload).
   * V2 only persists confidential bids to that orderbook.
   */
  private shouldPersistCollectionBidToListingsScraper(confidential: boolean | undefined): boolean {
    return (confidential ?? false) || this.environment.collection === 'cryptopunks-v1';
  }

  async saveCollectionBid(bidData: {
    bidderAddress: string;
    bidAmountWei: string;
    bidAmountEth: number;
    merkleRoot: string;
    accountNonce: number;
    bidNonce: number;
    signature: string;
    punk_indices?: number[];
    filterTag?: string;
    proofs?: Record<number, `0x${string}`[]>;
    expirationTimestamp?: number;
    expiresAtIso?: string;
    isTargetedBid?: boolean;
    selectedPunkIds?: number[];
    bidType?: BidType;
    confidential?: boolean;
    limitedPublicNotification?: boolean;
    stashBidProtectionEnabled?: boolean;
    punkBid?: {
      order: {
        numberOfUnits: number;
        pricePerUnit: string;
        auction: string;
      };
      accountNonce: string;
      bidNonce: string;
      expiration: string;
      root: string;
      verifyingContract: string;
    };
    typedDataDomain?: TypedDataDomain;
  }): Promise<void> {
    try {
      if (!this.shouldPersistCollectionBidToListingsScraper(bidData.confidential)) {
        console.log(
          '[BidCreationModal] Skipping orderbook save (listings scraper): public bids on V2 are not indexed here.',
        );
        return;
      }

      // Extract base URL from alternativeListingsGraphURI
      const graphUri = this.environment.alternativeListingsGraphURI || '';
      const baseUrl = graphUri.replace(/\/graphql$/, '');

      if (!baseUrl) {
        throw new Error('alternativeListingsGraphURI is not configured');
      }

      // Generate unique bid ID
      const bidId = `collection-bid-${bidData.bidderAddress.toLowerCase()}-${bidData.bidNonce}`;

      // Calculate expiration date based on user selection
      const createdAt = new Date();
      let expiresAt = new Date(createdAt);
      if (bidData.expiresAtIso) {
        expiresAt = new Date(bidData.expiresAtIso);
      } else if (bidData.expirationTimestamp) {
        expiresAt = new Date(bidData.expirationTimestamp * 1000);
      } else {
        const selectedExpiryDays = this.collectionBidExpiryModel?.value ?? 7;
        expiresAt.setDate(expiresAt.getDate() + selectedExpiryDays);
      }

      // Prepare payload
      const payload: any = {
        id: bidId,
        bidder_address: bidData.bidderAddress,
        punk_indices: bidData.punk_indices || [],
        tag: bidData.filterTag || '',
        bid_amount_wei: bidData.bidAmountWei,
        bid_amount_eth: bidData.bidAmountEth,
        merkle_root: bidData.merkleRoot,
        account_nonce: bidData.accountNonce,
        bid_nonce: bidData.bidNonce,
        status: 'pending',
        created_at: createdAt.toISOString(),
        proofs: bidData.proofs || {},
        expires_at: expiresAt.toISOString(),
        signature: bidData.signature,
        source: this.environment.collection === 'cryptopunks' ? 'cryptopunks.eth.limo' : 
                this.environment.collection === 'cryptopunks-v1' ? 'v1cryptopunks.com' : 
                'cryptopunks.eth.limo', // fallback
        punk_bid: bidData.punkBid || null,
        typed_data_domain: bidData.typedDataDomain || null,
        confidential: bidData.confidential || false,
        limited_public_notification: bidData.limitedPublicNotification ?? true,
      };

      if (this.environment.featureFlags?.STASH_BID_PROTECTION === true) {
        payload.stash_bid_protection_enabled = bidData.stashBidProtectionEnabled !== false;
      }

      // Add targeted bid specific fields
      if (bidData.isTargetedBid) {
        payload.bid_type = 'targeted';
        if (bidData.selectedPunkIds && bidData.selectedPunkIds.length > 0) {
          payload.selected_punk_ids = bidData.selectedPunkIds;
        }
      } else if (bidData.bidType === 'owner') {
        payload.bid_type = 'owner';
      } else if (bidData.bidType === 'trait') {
        payload.bid_type = 'trait';
      }

      const apiUrl = `${baseUrl}/api/scraper/punk-bids/create`;
      console.log('[BidCreationModal] Saving collection bid to:', apiUrl);

      // Make POST request
      await firstValueFrom(
        this.http.post(apiUrl, payload, {
          headers: {
            'Content-Type': 'application/json'
          }
        })
      );

      console.log('[BidCreationModal] Collection bid saved successfully');
    } catch (error) {
      console.error('[BidCreationModal] Error saving collection bid:', error);
      throw error;
    }
  }

  // Reset modal state
  private resetModalState(): void {
    this.refreshDualBidPlatformFlags();
    this.selectedBidPlatform = this.dualBidPlatformEnabled ? 'onchain' : 'stash';
    this.isOnChainPlatformSelected = this.selectedBidPlatform === 'onchain';
    this.syncStepperLabels();
    this.onChainSettlementEth = '0';
    this.showOnChainSettlementReward = false;
    this.onChainBidSubmitting = false;
    this.onChainBidPlacedSuccessfully = false;
    this.placedOnChainTxHash = null;
    this.placedOnChainTxShortHash = null;
    this.currentStep = 'check';
    this.collectionBidAmount = '';
    this.stashAddress = null;
    this.stashDeployed = false;
    this.stashUpgradeNeeded = false;
    this.stashOnChainVersion = null;
    this.factoryTargetVersion = null;
    this.checkingStash = true;
    this.collectionBidExpiryModel = this.collectionBidExpiryOptions[2];
    this.confidential = false;
    this.sendLimitedNotification = true;
    this.disguiseTraitBidAsCustom = false;
    this.disguiseBidConfirmationActive = false;
    this.publishOnCryptopunksApp = true;
    this.stashBidProtectionEnabled = true;
    this.publishStatusLimo = 'pending';
    this.publishStatusApp = 'pending';
    this.publishErrorLimo = '';
    this.publishErrorApp = '';
    this.showCryptopunksAppPublish = false;
    this.cryptopunksAppPayload = null;
    this.clearStashLiquidityState();
  }

  // Close modal
  closeModal(): void {
    const wasPublishing = this.currentStep === 'publish';
    this.resetModalState();
    if (wasPublishing) {
      this.bidCreated.emit();
    }
    this.modalClosed.emit();
  }

  // Close transaction tracking
  closeTransactionTracking(): void {
    this.txTrackingSvc.closeTxModal();
    this.isTransactionActive = false;
  }
}

